// src/firebaseConfig.js - Optimized for Performance (v8 Compatible)

// ENTERPRISE: Robust Firebase v8 imports aligned with project architecture
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/database';

// CRITICAL: Ensure Firebase is globally available immediately
if (typeof window !== 'undefined') {
  window.firebase = firebase;
  console.log('✅ Firebase made globally available:', !!window.firebase);
}

// --- Configuration ---
console.log("Initializing Firebase Config. Environment:", process.env.NODE_ENV);

// PRODUCTION SECURITY: Environment variables for production, fallbacks for development
const firebaseConfig = {
  apiKey: process.env.FIREBASE_API_KEY || "AIzaSyBwrAbvGDs8YoAAjZghUb2ApkQI9QT8be0",
  authDomain: process.env.FIREBASE_AUTH_DOMAIN || "project-aaa-eeaff.firebaseapp.com",
  projectId: process.env.FIREBASE_PROJECT_ID || "project-aaa-eeaff",
  storageBucket: process.env.FIREBASE_STORAGE_BUCKET || "project-aaa-eeaff.firebasestorage.app",
  messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID || "531219919004",
  appId: process.env.FIREBASE_APP_ID || "1:531219919004:web:9fe9833b46a7e9d19fbd59",
  measurementId: process.env.FIREBASE_MEASUREMENT_ID || "G-TDMHVKDZRP",
  databaseURL: process.env.FIREBASE_DATABASE_URL || "https://project-aaa-eeaff-default-rtdb.europe-west1.firebasedatabase.app"
};

// PRODUCTION SECURITY: Only validate in production environment
if (process.env.NODE_ENV === 'production') {
  const requiredEnvVars = [
    'FIREBASE_API_KEY',
    'FIREBASE_AUTH_DOMAIN', 
    'FIREBASE_PROJECT_ID',
    'FIREBASE_STORAGE_BUCKET',
    'FIREBASE_MESSAGING_SENDER_ID',
    'FIREBASE_APP_ID',
    'FIREBASE_DATABASE_URL'
  ];

  const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);
  if (missingVars.length > 0) {
    console.error('🚨 PRODUCTION ERROR: Missing required Firebase environment variables:', missingVars);
    throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
  }
  console.log('✅ Production environment variables validated');
} else {
  console.log('📝 Development mode: Using fallback credentials for local testing');
}

// Ensure HTTPS for databaseURL
if (firebaseConfig.databaseURL && firebaseConfig.databaseURL.startsWith('http:')) {
  firebaseConfig.databaseURL = firebaseConfig.databaseURL.replace('http:', 'https:');
}

console.log("Using Firebase Database URL:", firebaseConfig.databaseURL);

const FIREBASE_OPERATIONS_TIMEOUT = 10000; // 10 seconds timeout for operations

// --- ENTERPRISE: Robust Firebase App Initialization ---
let firebaseApp;
try {
  // Ensure Firebase is available before initialization
  if (typeof firebase === 'undefined' || !firebase) {
    throw new Error('Firebase not available - bundling issue detected');
  }
  
  console.log('🔍 Firebase available:', !!firebase, 'Apps:', firebase.apps?.length || 0);
  
  if (firebase.apps.length === 0) {
    console.log("Initializing new Firebase app...");
    firebaseApp = firebase.initializeApp(firebaseConfig);
  } else {
    console.log("Using existing Firebase app.");
    firebaseApp = firebase.app();
  }
  
  if (!firebaseApp || !firebaseApp.options) {
    throw new Error("App initialization failed - invalid app instance");
  }
  
  console.log("✅ Firebase app initialized successfully");
  
  // Make Firebase globally available for debugging AND enterprise validation
  window.firebase = firebase;
  window.firebaseApp = firebaseApp;
  window.auth = auth;
  window.db = db;
  
  console.log('🔧 Firebase globals set immediately for enterprise validation:', {
    firebase: !!window.firebase,
    auth: !!window.auth,
    db: !!window.db,
    signInMethod: typeof window.auth?.signInWithEmailAndPassword
  });
  
} catch (error) {
  console.error("🚨 CRITICAL: Firebase app initialization failed:", error);
  console.error("🚨 Firebase bundle status:", {
    firebaseExists: typeof firebase !== 'undefined',
    firebaseApps: firebase?.apps?.length || 'N/A',
    config: firebaseConfig
  });
  
  // Provide a minimal fallback to prevent downstream errors
  firebaseApp = { options: firebaseConfig, name: "[fallback-app]" };
}

// --- Service Initialization with Fallbacks (v8 Compatible) ---
let auth, db, realtimeDB;

try {
  auth = firebase.auth();
  console.log("Firebase Auth service initialized.");
  
  // ENTERPRISE: Comprehensive Firebase error handling
  window.addEventListener('error', (event) => {
    if (event.message && event.message.includes('Network Error') && event.filename && event.filename.includes('firebase')) {
      console.warn('Firebase network error caught and handled:', event.message);
      event.preventDefault(); // Prevent error from propagating
    }
  });
  
  // Handle Firebase auth errors specifically
  auth.onAuthStateChanged((user) => {
    // Clear any corrupted auth localStorage on auth state change
    if (user) {
      // User signed in - clear any error states
      localStorage.removeItem('authError');
      localStorage.removeItem('signInAttempts');
      console.log('✅ Firebase auth successful - error states cleared');
    }
  }, (error) => {
    console.error('Firebase auth state change error:', error);
    
    // Handle specific Firebase auth errors
    if (error.code === 'auth/network-request-failed') {
      console.warn('🌐 Firebase network error - will retry');
    } else if (error.code === 'auth/too-many-requests') {
      console.warn('🚫 Too many auth requests - temporary throttling');
      localStorage.setItem('authError', 'too-many-requests');
    } else {
      console.error('🚨 Firebase auth error:', error.code, error.message);
      localStorage.setItem('authError', error.code);
    }
  });
  
  // Set persistence preference (SESSION means user stays logged in until browser tab closes)
  auth.setPersistence(firebase.auth.Auth.Persistence.SESSION)
  .then(() => {
      console.log("Auth persistence set to SESSION.");
      // Now potentially attach the main listener IF it's done here (usually done in index.js)
  })
  .catch(err => console.error("Error setting auth persistence:", err));
} catch (error) {
  console.error("Error initializing Firebase Auth:", error);
  auth = { // Minimal fallback auth object
    currentUser: null,
    onAuthStateChanged: (cb) => { console.warn("Auth fallback: onAuthStateChanged called."); cb(null); return () => {}; },
    signInWithEmailAndPassword: () => Promise.reject(new Error("Auth not initialized")),
    createUserWithEmailAndPassword: () => Promise.reject(new Error("Auth not initialized")),
    signOut: () => { console.warn("Auth fallback: signOut called."); return Promise.resolve(); }
  };
}

try {
  db = firebase.firestore();
  console.log("Firestore service initialized.");
} catch (error) {
  console.error("Error initializing Firestore:", error);
  db = { // Minimal fallback firestore object
    collection: (path) => {
        console.warn(`Firestore fallback: collection('${path}') called.`);
        return {
            doc: () => ({ get: () => Promise.resolve({ exists: false }), set: () => Promise.reject(new Error("Firestore not initialized")) }),
            where: () => ({ get: () => Promise.resolve({ empty: true, docs: [] }) }),
            orderBy: () => ({ limit: () => ({ get: () => Promise.resolve({ empty: true, docs: [] }) }) }),
            add: () => Promise.reject(new Error("Firestore not initialized"))
        };
     }
  };
}

try {
  realtimeDB = firebase.database();
  console.log("Realtime Database service initialized.");

  // Attempt to go online initially
  // realtimeDB.goOnline(); REMOVED AFTER TESTING, NOT NEEDED FOR INITIALIZATION

  // Minimal connection status logging (no aggressive reconnect logic)
  const connectedRefMinimal = realtimeDB.ref(".info/connected");
  connectedRefMinimal.on("value", (snap) => {
    console.info(`Firebase RTDB Connection Status: ${snap.val() ? 'Connected' : 'Disconnected'}`);
    // Optional: Trigger a custom event for UI updates on connection change
    // if (typeof window !== 'undefined') {
    //    window.dispatchEvent(new CustomEvent('firebase-connection-status', { detail: { connected: snap.val() } }));
    // }
  }, (error) => {
      console.error("Error listening to RTDB connection status:", error);
  });

} catch (error) {
  console.error("Error initializing Realtime Database:", error);
  realtimeDB = { // Minimal fallback RTDB object
    ref: (path) => {
        console.warn(`RTDB fallback: ref('${path}') called.`);
        return {
            on: () => {},
            once: () => Promise.resolve({ val: () => null, exists: () => false }),
            set: () => Promise.reject(new Error("RTDB not initialized")),
            update: () => Promise.reject(new Error("RTDB not initialized")),
            push: () => ({ key: 'fallback_key', set: () => Promise.reject(new Error("RTDB not initialized")) }),
            remove: () => Promise.reject(new Error("RTDB not initialized")),
            off: () => {}
        };
    },
    goOnline: () => console.warn("RTDB fallback: goOnline called."),
    goOffline: () => console.warn("RTDB fallback: goOffline called.")
  };
}


// --- Helper Functions ---

/**
 * Checks the Firebase Realtime Database connection status reliably.
 * @param {number} timeoutMs - Timeout for the check.
 * @returns {Promise<boolean>} True if connected, false otherwise.
 */
export function isDatabaseConnected(timeoutMs = 5000) {
  return new Promise((resolve) => {
    if (!realtimeDB || typeof realtimeDB.ref !== 'function') {
      console.warn("isDatabaseConnected: realtimeDB not available.");
      return resolve(false);
    }

    const timeoutId = setTimeout(() => {
      console.warn("isDatabaseConnected: check timed out.");
      checkRef.off("value", onValueChange); // Important: detach listener on timeout
      resolve(false);
    }, timeoutMs);

    const checkRef = realtimeDB.ref(".info/connected");

    const onValueChange = (snap) => {
      clearTimeout(timeoutId);
      checkRef.off("value", onValueChange); // Detach listener immediately after getting value
      resolve(snap.val() === true);
    };

    checkRef.on("value", onValueChange, (error) => {
      clearTimeout(timeoutId);
      checkRef.off("value", onValueChange); // Detach listener on error
      console.error("isDatabaseConnected check failed:", error);
      resolve(false); // Assume not connected on error
    });
  });
}

/**
 * Attempts to force a reconnection to the Firebase Realtime Database.
 * @returns {Promise<boolean>} True if connected after the attempt, false otherwise.
 */
export async function forceReconnect() {
  if (!realtimeDB || typeof realtimeDB.goOffline !== 'function') {
      console.warn("forceReconnect: realtimeDB not available.");
      return false;
  }
  try {
    console.log("Attempting forceReconnect: goOffline -> goOnline...");
    realtimeDB.goOffline();
    await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay
    realtimeDB.goOnline();
    await new Promise(resolve => setTimeout(resolve, 1500)); // Wait for potential connection
    const connected = await isDatabaseConnected();
    console.log("forceReconnect result:", connected);
    return connected;
  } catch (error) {
    console.error("Error during forced reconnection:", error);
    return false;
  }
}

/**
 * Waits for the Realtime Database connection to be established.
 * Useful during initial app load or after attempting reconnection.
 * @param {number} timeoutMs - Maximum time to wait.
 * @returns {Promise<boolean>} True if connection established, false if timed out.
 */
export async function waitForDatabaseReady(timeoutMs = 10000) {
  return new Promise((resolve, reject) => {
    let checkInterval;
    const startTime = Date.now();

    const check = async () => {
      if (Date.now() - startTime > timeoutMs) {
        clearInterval(checkInterval);
        console.error("Timeout waiting for database connection.");
        return reject(new Error('Timeout waiting for database connection'));
      }

      const connected = await isDatabaseConnected(1000); // Use shorter timeout for checks
      if (connected) {
        clearInterval(checkInterval);
        console.log("Database connection ready.");
        resolve(true);
      }
      // If not connected, the interval will just try again
    };

    // Check immediately, then set interval
    check();
    checkInterval = setInterval(check, 1500); // Check every 1.5 seconds
  });
}


// --- Exports ---
export { firebase, auth, db, realtimeDB, firebaseConfig };
export default firebase;
/*// Convenience export for onAuthStateChanged listener
export const onAuthStateChanged = (callback) => {
    if (auth && typeof auth.onAuthStateChanged === 'function') {
        return auth.onAuthStateChanged(callback);
    } else {
        console.error("Auth service not available for onAuthStateChanged.");
        // Call back immediately with null and return a no-op unsubscribe function
        callback(null);
        return () => {};
    }
};
*/
